import { Badge, Dropdown, RadioButton } from "@eudonet/opal";
import { Fragment, h } from "vue";
import { detectCountryFormat, formatAddress } from "@/shared/utils/formatAddress";
import { countryNames } from "@/shared/utils/addressCountry";
import { TruncatedCell } from "@/shared/components/TruncatedCell";
export { TruncatedCell };
/** Maps a `ContactAddressType` to its OPAL badge colour. */
export function contactAddressBadgeTheme(type) {
    switch (type) {
        case "Home":
        case "Headquarters":
            return "green";
        case "Work":
        case "Branch":
            return "violet";
        case "Billing":
            return "orange";
        case "Shipping":
        default:
            return "neutral";
    }
}
/**
 * Builds the three shared address-table columns (full address / type badge /
 * primary radio). Identical markup, i18n keys, a11y sr-only label, and
 * source-branched emit logic in both the widget and the expand modal.
 */
export function buildContactAddressColumns(opts) {
    const { t, primaryId, onSetPrimary, radioPrefix = "primary", canEdit = false, onEdit, onDelete } = opts;
    const names = countryNames(t);
    const fullAddress = (row) => formatAddress(row, detectCountryFormat(row.country, row.region), names);
    const radioIdPrefix = `radio-${radioPrefix}`;
    const radioName = radioPrefix === "primary" ? "primary-address" : "primary-address-modal";
    const columns = [
        {
            id: "address",
            header: t("contact.detail.addresses.columns.fullAddress"),
            cell: (row) => h(TruncatedCell, {
                text: fullAddress(row),
                textClass: "text-sm text-(--text-text-black-default)",
            }),
            width: "min-w-40",
        },
        {
            id: "type",
            header: t("contact.detail.addresses.columns.type"),
            width: "w-46",
            sortable: true,
            sortBy: (row) => row.type,
            cell: (row) => 
            // A type-less (personal, picker-free) address is stored as "Unknown" — it has
            // no badge label, so render a muted dash rather than a missing-key badge.
            row.type === "Unknown"
                ? h("span", { class: "text-sm text-(--text-text-body)" }, "—")
                : h(Badge, {
                    theme: contactAddressBadgeTheme(row.type),
                    label: t(`contact.detail.addresses.types.${row.type}`),
                    showCloseIcon: false,
                    size: "sm",
                    truncate: false,
                }),
        },
        {
            id: "isPrimary",
            header: t("contact.detail.addresses.columns.isPrimary"),
            width: "w-28",
            sortable: true,
            sortBy: (row) => (row.id === primaryId ? "0" : "1"),
            cell: (row) => h(Fragment, null, [
                h("label", {
                    for: `${radioIdPrefix}-${row.id}`,
                    class: "sr-only",
                }, t("contact.detail.addresses.columns.setPrimaryAriaLabel", {
                    address: fullAddress(row),
                })),
                h(RadioButton, {
                    id: `${radioIdPrefix}-${row.id}`,
                    modelValue: primaryId,
                    value: row.id,
                    name: radioName,
                    disabled: !canEdit,
                    "onUpdate:modelValue": () => {
                        // OPAL's RadioButton emits update:modelValue on every click of its container,
                        // even when the row is already the selected one (it's not a native radio input
                        // under the hood). Without this guard, clicking the already-primary row's radio
                        // re-triggers the set-primary call and shows a misleading success toast even
                        // though nothing changed (OP-6395).
                        if (row.id === primaryId)
                            return;
                        // Branch on source, not on linkId presence: a Linked row always carries a
                        // linkId (the BFF sets it for every linked address). Falling back to Direct
                        // when it's missing would silently set the wrong primary — instead emit the
                        // link id (empty if the contract is violated) so the failure is visible.
                        const target = row.source === "Linked"
                            ? { kind: "Linked", id: row.linkId ?? "" }
                            : { kind: "Direct", id: row.id };
                        onSetPrimary(target);
                    },
                }),
            ]),
        },
    ];
    // Actions column — only for canEdit. Read-only rows never expose the menu.
    // Linked (professional) addresses expose only Delete (OP-5852); editing is not supported.
    // Direct (personal) addresses expose Edit + Delete.
    if (canEdit && (onEdit || onDelete)) {
        columns.push({
            id: "actions",
            header: "",
            width: "w-18",
            cell: (row) => {
                // isReadOnly applies only to Direct addresses (their data cannot be edited).
                // Linked addresses are always isReadOnly=true (data managed by the org) but
                // the link itself can still be deleted — let the Linked branch below handle it.
                if (row.isReadOnly && row.source !== "Linked")
                    return h("span");
                if (row.source === "Linked") {
                    if (!onDelete)
                        return h("span");
                    return h(Dropdown, {
                        ghost: true,
                        label: "",
                        triggerIconLeft: "ri-more-line",
                        menuAlign: "right",
                        menuWidth: "127px",
                        ariaLabel: t("contact.detail.addresses.rowActions"),
                        items: [
                            { label: t("contact.detail.addresses.actions.delete"), icon: "ri-delete-bin-line", danger: true, onClick: () => onDelete(row) },
                        ],
                    });
                }
                return h(Dropdown, {
                    ghost: true,
                    label: "",
                    triggerIconLeft: "ri-more-line",
                    menuAlign: "right",
                    menuWidth: "127px",
                    ariaLabel: t("contact.detail.addresses.rowActions"),
                    items: [
                        ...(onEdit
                            ? [{ label: t("contact.detail.addresses.actions.edit"), icon: "ri-pencil-line", onClick: () => onEdit(row) }]
                            : []),
                        ...(onDelete
                            ? [{ label: t("contact.detail.addresses.actions.delete"), icon: "ri-delete-bin-line", danger: true, onClick: () => onDelete(row) }]
                            : []),
                    ],
                });
            },
        });
    }
    return columns;
}
